import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import {
  loadBulkAgentless,
  loadBulkProperties,
  loadMultipleQrForm,
  postDownload,
  saveBulkAgentless,
  saveBulkProperties,
  unassignedDevices,
  type BulkAgentlessPage,
  type BulkPropertiesPage,
} from '../api/inventoree'
import { ApiError } from '../api/client'
import { DatePicker } from '../ui/DatePicker'
import { toast } from '../ui/notify'

/**
 * Widgetul de selectie multipla (devicesWidget.php / agentlessWidget.php):
 * Export QR incarca formularul v4, Settings e formularul identic.
 */

function rightsToast(err: unknown) {
  const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'An error occured! Please try again..'
  toast(message, 'Error', 'error')
}

export function BulkProperties({ ids }: { ids: number[] }) {
  const [page, setPage] = useState<BulkPropertiesPage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [brand, setBrand] = useState('')
  const [acquisition, setAcquisition] = useState('')
  const [warranty, setWarranty] = useState('')

  useEffect(() => {
    let gone = false
    setPage(null)
    setError(null)
    void loadBulkProperties(ids)
      .then((next) => {
        if (!gone) setPage(next)
      })
      .catch((err: unknown) => {
        if (!gone) setError(err instanceof Error ? err.message : 'Error')
      })
    return () => {
      gone = true
    }
  }, [ids.join(',')])

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (!confirm('Are you sure?')) return
    try {
      const saved = await saveBulkProperties(page?.devices ?? ids, {
        device_brand: brand,
        device_acquisition_date: acquisition,
        device_warranty_expiry_date: warranty,
      })
      if (saved.ok === 1) toast('Changes Saved!', 'Success', 'success')
      else toast('An error occured! Please try again..', 'Error', 'error')
    } catch (err) {
      rightsToast(err)
    }
  }

  if (error) return <div className="alert alert-danger">{error}</div>
  if (!page) return <div className="loading">Loading...</div>

  return (
    <>
      <div className="notifications-box"></div>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Device</strong> properties
        </h4>
      </header>
      <div className="panel-body">
        <form className="form-horizontal labelcustomize multipleSelectSettings" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="/api/v2/inventoree/bulk/properties" onSubmit={submit}>
          <div className="form-group">
            <label style={{ textAlign: 'left' }} className="control-label col-md-3">
              <span className="color">Brand</span>
            </label>
            <div className="col-md-9">
              {page.devices.map((id) => (
                <input key={id} type="hidden" name="devices[]" value={id} />
              ))}
              <select className="form-control" name="device_brand" value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option>Select...</option>
                {page.brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label style={{ textAlign: 'left' }} className="control-label col-md-3">
              <span className="color">Acquisition date</span>
            </label>
            <div className="col-md-9">
              <DatePicker value={acquisition} name="device_acquisition_date" onChange={setAcquisition} icon="far fa-calendar-check ico" />
            </div>
          </div>
          <div className="form-group">
            <label style={{ textAlign: 'left' }} className="control-label col-md-3">
              <span className="color">Warranty expiry date</span>
            </label>
            <div className="col-md-9">
              <DatePicker value={warranty} name="device_warranty_expiry_date" onChange={setWarranty} />
            </div>
          </div>
          <div className="form-group offset">
            <div className="col-md-offset-3 col-md-9">
              <button type="submit" className="btn btn-theme">
                Update
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export function BulkAgentless({ ids }: { ids: number[] }) {
  const [page, setPage] = useState<BulkAgentlessPage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [type, setType] = useState('')
  const [owner, setOwner] = useState('')
  const [office, setOffice] = useState('')
  const [group, setGroup] = useState('')
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [classified, setClassified] = useState('')

  useEffect(() => {
    let gone = false
    setPage(null)
    setError(null)
    void loadBulkAgentless(ids)
      .then((next) => {
        if (!gone) setPage(next)
      })
      .catch((err: unknown) => {
        if (!gone) setError(err instanceof Error ? err.message : 'Error')
      })
    return () => {
      gone = true
    }
  }, [ids.join(',')])

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (!confirm('Are you sure?')) return
    try {
      const saved = await saveBulkAgentless(page?.devices ?? ids, {
        device_name: name,
        device_type: type,
        device_owner: owner,
        device_office: office,
        device_group: group,
        device_brand: brand,
        device_model: model,
        device_classified_info: classified,
      })
      if (saved.ok === 1) toast('Changes Saved!', 'Success', 'success')
      else toast('An error occured! Please try again..', 'Error', 'error')
    } catch (err) {
      rightsToast(err)
    }
  }

  const unassign = async () => {
    if (!confirm('Are you sure?')) return
    try {
      const saved = await unassignedDevices(page?.devices ?? ids)
      if (saved.ok === 1) toast('The devices has been unassigned.', 'Success', 'success')
      else toast('An error occured! Please try again..', 'Error', 'error')
    } catch (err) {
      rightsToast(err)
    }
  }

  if (error) return <div className="alert alert-danger">{error}</div>
  if (!page) return <div className="loading">Loading...</div>

  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Update</strong> settings
        </h4>
      </header>
      <div className="panel-body">
        <form action="/api/v2/inventoree/bulk/agentless" method="post" className="form-horizontal labelcustomize multipleSelectUpdateDevice" onSubmit={submit}>
          {page.devices.map((id) => (
            <input key={id} type="hidden" className="form-control" name="devices[]" id="devices" value={id} />
          ))}
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Name</span>
            </label>
            <div className="col-sm-9">
              <input type="text" className="form-control" name="device_name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Device Type</span>
            </label>
            <div className="col-sm-9">
              <select name="device_type" className="form-control" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">Select...</option>
                {page.types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{' '}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">User</span>
            </label>
            <div className="col-sm-9">
              <select name="device_owner" className="form-control" value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">Select...</option>
                {page.owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Office</span>
            </label>
            <div className="col-sm-9">
              <select name="device_office" className="form-control" value={office} onChange={(e) => setOffice(e.target.value)}>
                <option value="">Select...</option>
                {page.offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Group</span>
            </label>
            <div className="col-sm-9">
              <select name="device_group" className="form-control" value={group} onChange={(e) => setGroup(e.target.value)}>
                <option value="">Select...</option>
                {page.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Brand</span>
            </label>
            <div className="col-sm-9">
              <select name="device_brand" className="form-control" value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">Select...</option>
                {page.brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Model</span>
            </label>
            <div className="col-sm-9">
              <input type="text" className="form-control" name="device_model" value={model} onChange={(e) => setModel(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Classified info</span>
            </label>
            <div className="col-sm-9">
              <textarea className="form-control" name="device_classified_info" value={classified} onChange={(e) => setClassified(e.target.value)}></textarea>
            </div>
          </div>
          <div className="form-group offset">
            <div className="col-md-offset-3 col-md-9">
              <button type="submit" className="btn btn-theme">
                Update
              </button>
            </div>
          </div>
        </form>
        <div className="col-md-offset-3 col-md-9">
          <section className="settings">
            <button id="unassigned" className="btn btn-theme unassigned-device" style={{ marginLeft: -8 }} type="button" onClick={() => void unassign()}>
              Unassigned
            </button>
          </section>
        </div>
        <div className="notifications-box"></div>
      </div>
    </>
  )
}

export function SelectedDevicesWidget({
  shown,
  ids,
  onHide,
  settings = 'properties',
}: {
  shown: boolean
  ids: number[]
  onHide(): void
  settings?: 'properties' | 'agentless'
}) {
  const [tab, setTab] = useState<'none' | 'qr' | 'settings'>('none')
  const [html, setHtml] = useState('')
  const settingsHref = settings === 'agentless' ? '/api/v2/inventoree/bulk/agentless-form' : '/api/v2/inventoree/bulk/properties-form'

  /**
   * PRIMA FILA E EXPORT QR, SI SE DESCHIDE SINGURA.
   *
   * In portal, `MultipleActions.widgetContent` (public/js/general.js) se
   * termina cu `tab.eq(0).trigger('click')`: la fiecare schimbare de bife,
   * widgetul se arata deschis pe prima fila din lista - Export QR - cu
   * formularul ei deja cerut de la server. Aici la fel, fara jQuery: cat timp
   * widgetul e la vedere si are macar o masina bifata, fila e `qr` si
   * formularul se reincarca pe noua selectie.
   */
  useEffect(() => {
    if (!shown || ids.length === 0) return
    let gone = false
    setTab('qr')
    setHtml('')
    void loadMultipleQrForm(ids)
      .then((next) => {
        if (!gone) setHtml(next)
      })
      .catch((err: unknown) => rightsToast(err))
    return () => {
      gone = true
    }
  }, [shown, ids.join(',')])

  const openQr = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault()
    setTab('qr')
    void loadMultipleQrForm(ids)
      .then(setHtml)
      .catch((err: unknown) => rightsToast(err))
  }

  const openSettings = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault()
    setTab('settings')
  }

  const onContentClick = (ev: MouseEvent<HTMLDivElement>) => {
    const btn = (ev.target as HTMLElement).closest('.export_multi_qr')
    if (!(btn instanceof HTMLElement)) return
    ev.preventDefault()
    const form = btn.closest('form')
    if (!form) return
    void postDownload('/inventoree/qr/export-multiple', new FormData(form)).catch((err: unknown) => rightsToast(err))
  }

  return (
    <div className="multiple_check_widget widget border-brown" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-brown">
        <div className="widget-icon">
          <i className="fas fa-building"></i>
        </div>
        <h2 className="widget-title">Selected Devices Options</h2>
        <div className="widget-action">
          <button className="btn hide-widget" type="button" onClick={onHide}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left">
            <li data-class-nav="nav-content" data-toggle="tab" className={tab === 'qr' ? 'active' : undefined}>
              <a href="/api/v2/inventoree/qr/multiple-form" onClick={openQr}>
                Export QR
              </a>
            </li>
            <li data-class-nav="nav-content" data-toggle="tab" className={tab === 'settings' ? 'active' : undefined}>
              <a href={settingsHref} onClick={openSettings}>
                Settings
              </a>
            </li>
          </ul>
        </div>
        {tab === 'qr' ? (
          <div
            className="nav-content"
            style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}
            onClick={onContentClick}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className="nav-content" style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}>
            {tab === 'settings' && settings === 'properties' ? <BulkProperties ids={ids} /> : null}
            {tab === 'settings' && settings === 'agentless' ? <BulkAgentless ids={ids} /> : null}
          </div>
        )}
      </div>
    </div>
  )
}
